import type { ApiDashboardElement, SourceMap } from '@highimpact-frontend/api-types';
import {
  DEFAULT_DECIMATION_POLICY,
  buildRequestKey,
  type DecimationPolicy,
} from '@highimpact/react-hooks';
import { useEffect, useMemo, useState } from 'react';

import { useSourceLabelsQuery } from '../../../../query/useSourceLabelsQuery';
import {
  getElementMeasureKind,
  usesMultiIntervalMeasureFetch,
} from '../../../../runtime/elementMeasureSupport';
import type { MeasureRow } from '../../../../runtime/measureRow.types';
import { getIntervals } from '../../../../state/dashboard-element-operations';
import {
  useElementMeasureTransport,
  type ElementMeasurePlotType,
} from './useElementMeasureTransport';

export interface UseMultiIntervalElementMeasureRowsOptions {
  readonly element: ApiDashboardElement;
  readonly decimationPolicy?: DecimationPolicy;
  readonly sourceLabelMap?: SourceMap;
}

export interface UseMultiIntervalElementMeasureRowsResult {
  readonly dataByInterval: MeasureRow[][] | null;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly usesElementMeasureRequest: boolean;
  readonly labelMap: SourceMap;
}

/**
 * Loads one measure response per configured interval.
 */
export function useMultiIntervalElementMeasureRows({
  element,
  decimationPolicy = DEFAULT_DECIMATION_POLICY,
  sourceLabelMap: sourceLabelMapOverride,
}: UseMultiIntervalElementMeasureRowsOptions): UseMultiIntervalElementMeasureRowsResult {
  const {
    labelMap: queriedLabelMap,
    loading: labelsLoading,
    error: labelsError,
  } = useSourceLabelsQuery(element);
  const sourceLabelMap = sourceLabelMapOverride ?? queriedLabelMap;

  const measureKind = getElementMeasureKind(element);
  const plotTypeOverride =
    measureKind === 'table'
      ? undefined
      : (measureKind as ElementMeasurePlotType | undefined);

  const {
    transport,
    measureRequestSignature,
    usesElementMeasureRequest,
  } = useElementMeasureTransport({
    element,
    plotType: plotTypeOverride,
    decimationPolicy,
    sourceLabelMap,
  });

  const intervals = useMemo(() => getIntervals(element) ?? [], [element]);
  const [dataByInterval, setDataByInterval] = useState<MeasureRow[][] | null>(
    null
  );
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);

  useEffect(() => {
    if (
      !usesElementMeasureRequest ||
      !usesMultiIntervalMeasureFetch(measureKind)
    ) {
      setDataByInterval(null);
      setFetchLoading(false);
      setFetchError(null);
      return;
    }

    if (labelsLoading) {
      return;
    }

    if (intervals.length === 0) {
      setDataByInterval([]);
      setFetchLoading(false);
      setFetchError(null);
      return;
    }

    let cancelled = false;
    setFetchLoading(true);
    setFetchError(null);

    // Fetch intervals sequentially: the session-cache transport shares one
    // useDataLoader instance, and concurrent updateMainRequests supersede each other.
    void (async () => {
      try {
        const results: MeasureRow[][] = [];
        for (let index = 0; index < intervals.length; index += 1) {
          if (cancelled) {
            return;
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const interval = intervals[index]!;
          const rows = await transport.fetch({
            type: 'with-cache',
            slot: 'overview',
            timeRange: interval,
            decimation: decimationPolicy,
            replace: true,
            requestKey: `${buildRequestKey('overview', interval, decimationPolicy)}:${measureRequestSignature ?? measureKind ?? 'multi'}:${index}`,
          });
          results.push(rows);
        }
        if (!cancelled) {
          setDataByInterval(results);
          setFetchLoading(false);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setFetchError(
            error instanceof Error ? error : new Error(String(error))
          );
          setFetchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    decimationPolicy,
    intervals,
    labelsLoading,
    measureKind,
    measureRequestSignature,
    transport,
    usesElementMeasureRequest,
  ]);

  return {
    dataByInterval,
    loading: labelsLoading || fetchLoading,
    error: labelsError ?? fetchError,
    usesElementMeasureRequest,
    labelMap: sourceLabelMap,
  };
}
